import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, CheckCircle2, Terminal } from "lucide-react";
import { HomeHero } from "@/components/HomeHero";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { StatsBand, CardGrid, Methodology, Eyebrow, Icon, Resources } from "@/components/ServiceSections";
import { TrustCommitments } from "@/components/TechSections";
import { NetworkMesh } from "@/components/NetworkMesh";
import { AttackLifecycle } from "@/components/AttackLifecycle";
import { IMAGES, INDUSTRIES, METHODOLOGY_GENERIC, TRUST_COMMITMENTS } from "@/content/services";

const HOME_STATS = [
  { value: "24/7", label: "Managed detection & response" },
  { value: "Up to 80%", label: "Faster threat detection with a managed SOC" },
  { value: "100%", label: "Compliance control mapping on every engagement" },
  { value: "Zero", label: "Per-seat licence fees on our Open Source SOC Model" },
];

const SERVICE_CARDS = [
  { to: "/services/vapt", icon: "ShieldCheck", title: "VAPT", copy: "Find the weaknesses before attackers do. Manual and tool-assisted vulnerability assessment and penetration testing across web, network, cloud, and APIs, with remediation guidance your engineers can act on." },
  { to: "/services/managed-security", icon: "Radar", title: "Managed Security Services", copy: "Your SOC, our engineers. Choose SOC as a Service for fully managed 24/7 monitoring and response, or SOC Deployment to run our SOC stack inside your own environment." },
  { to: "/services/solutions", icon: "Boxes", title: "Cybersecurity Solutions", copy: "Enterprise security architecture done right. Landing Zones, SIEM, XDR, and Cloud Security designed, deployed, and operated to your compliance requirements." },
  { to: "/services/open-source-soc", icon: "HeartHandshake", title: "Open Source SOC Model", copy: "Compliance shouldn't require an enterprise budget. Our open-source SOC framework helps small and medium Indian organizations meet DPDP, CERT-In, and sector regulations affordably." },
];

const WHY = [
  { icon: "Wrench", title: "Engineer-led", copy: "Every engagement is run by practitioners who build and operate security controls, not just audit them." },
  { icon: "Scale", title: "Compliance-aligned", copy: "Everything we deliver is mapped to the frameworks you're audited against, so security work doubles as audit evidence." },
  { icon: "Layers", title: "Flexible delivery", copy: "Fully managed, deployed in your environment, or open source. You choose the model that fits your team and budget." },
  { icon: "Building2", title: "Built for regulated industries", copy: "Healthcare, finance, government, and high-growth companies with real regulatory exposure." },
];

const OSS_POINTS = ["Mapped to Indian compliance requirements", "Deployable on-premises or in cloud", "Backed by Lanvaus support and managed options"];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function Home() {
  return (
    <div data-testid="home-page">
      <HomeHero />

      <StatsBand stats={HOME_STATS} testId="home-stats-band" />

      <section id="services-overview" className="relative mx-auto max-w-7xl px-5 py-16 sm:py-24 sm:px-8 lg:px-16 lg:py-28" data-testid="services-overview">
        <div className="glow-orb right-0 top-0 h-[360px] w-[360px] bg-[#4C5A8C]/15" aria-hidden />
        <Reveal>
          <Eyebrow data-testid="services-eyebrow">What We Do</Eyebrow>
          <h2 className="mt-5 max-w-2xl font-display text-[1.75rem] font-bold leading-[1.2] tracking-tight text-white sm:text-4xl sm:leading-tight lg:text-[2.75rem] lg:leading-[1.12]">
            Four disciplines. <span className="text-[#8E9AEA]">One focus: security.</span>
          </h2>
        </Reveal>
        <div className="mt-10 sm:mt-14">
          <CardGrid cards={SERVICE_CARDS} cols="md:grid-cols-2" testPrefix="service-card" linkable />
        </div>

        <div className="mt-8 border-t border-white/10 pt-6" data-testid="home-all-services">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#A6B0C9]">
            Every service page
          </p>
          <div className="mt-4 flex flex-wrap gap-2.5">
            {[
              ["/services/vapt", "VAPT"],
              ["/services/managed-security", "Managed Security Services"],
              ["/services/solutions", "Cybersecurity Solutions"],
              ["/services/enterprise-landing-zone", "Enterprise Landing Zone"],
              ["/services/enterprise-siem", "Enterprise SIEM"],
              ["/services/enterprise-xdr", "Enterprise XDR"],
              ["/services/cloud-security", "Cloud Security"],
              ["/services/open-source-soc", "Open Source SOC Model"],
              ["/insights", "Insights & Blogs"],
              ["/about", "About Lanvaus"],
              ["/industries", "Industries We Secure"],
              ["/contact", "Contact"],
            ].map(([to, label]) => (
              <Link
                key={to}
                to={to}
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/12 bg-white/[0.03] px-3 py-2 text-xs font-medium text-[#DDE3F2] transition-colors duration-200 hover:border-[#8E9AEA]/50 hover:text-white"
                data-testid={`home-service-link-${to.replace(/\//g, "-").replace(/^-/, "")}`}
              >
                {label}
                <ArrowUpRight className="h-3 w-3 text-[#8E9AEA]" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden border-y border-white/10 bg-[#0A0E19]" data-testid="visibility-section">
        <div className="glow-orb -left-32 top-1/2 h-[460px] w-[460px] -translate-y-1/2 bg-[#4C5A8C]/20" aria-hidden />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 sm:py-24 sm:px-8 lg:grid-cols-[0.85fr_1.15fr] lg:px-16 lg:py-28">
          <Reveal>
            <Eyebrow>Single Pane of Glass</Eyebrow>
            <h2 className="mt-5 font-display text-[1.75rem] font-bold leading-[1.2] tracking-tight text-white sm:text-4xl sm:leading-tight lg:text-[2.75rem] lg:leading-[1.12]">
              Every signal, <span className="text-[#8E9AEA]">correlated in one place.</span>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#DDE3F2]">
              Endpoints, identities, email, network, cloud control planes, containers and OT all emit telemetry.
              Attackers count on those signals staying separate. We centralize and correlate them, so a login anomaly
              and a process launch become one incident instead of two ignored alerts.
            </p>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {["Cross-domain correlation", "MITRE ATT&CK coverage mapping", "Retention that satisfies auditors", "Human triage on every escalation"].map(
                (p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-[#DDE3F2]" data-testid={`visibility-point-${slug(p)}`}>
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#8E9AEA]" />
                    {p}
                  </li>
                )
              )}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="glass-card overflow-hidden p-4 sm:p-6">
              <div className="aspect-[16/10] w-full">
                <NetworkMesh />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <AttackLifecycle />

      <section className="relative overflow-hidden border-y border-[#8E9AEA]/20" data-testid="oss-soc-section">
        <img
          src={IMAGES.matrix}
          alt="Security operations dashboard showing correlated log data and compliance status"
          width="1920"
          height="1080"
          decoding="async"
          className="hero-photo absolute inset-0 h-full w-full object-cover opacity-60"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F1A] via-[#0B0F1A]/40 to-[#0B0F1A]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 py-16 sm:py-24 sm:px-8 lg:grid-cols-2 lg:px-16 lg:py-32">
          <Reveal>
            <Eyebrow data-testid="oss-soc-pill">Built for India's Small and Medium Businesses</Eyebrow>
            <h2 className="mt-5 font-display text-[1.75rem] font-bold leading-[1.2] tracking-tight text-white sm:text-4xl sm:leading-tight lg:text-[2.75rem] lg:leading-[1.12]">
              Enterprise-grade security operations, <span className="text-[#8E9AEA]">open source and compliance-ready.</span>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#DDE3F2] sm:text-lg">
              Most Indian SMBs face the same regulatory obligations as large enterprises, with a fraction of the budget. We built our Open Source SOC Model to close that gap: a ready-to-deploy security operations stack mapped to the DPDP Act 2023, CERT-In directions, IT Act requirements, and sector rules from RBI, SEBI, and IRDAI.
            </p>
            <ul className="mt-8 flex flex-col gap-3.5">
              {OSS_POINTS.map((point) => (
                <li key={point} className="flex items-start gap-3 text-sm text-[#DDE3F2]" data-testid={`oss-point-${slug(point)}`}>
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#8E9AEA]" />
                  {point}
                </li>
              ))}
            </ul>
            <Link to="/services/open-source-soc" className="btn-primary mt-10" data-testid="oss-soc-primary-cta">
              Explore the Open Source SOC Model
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="glass-card overflow-hidden !p-0" data-testid="oss-soc-terminal">
              <div className="flex items-center gap-2 border-b border-white/10 bg-white/[0.04] px-5 py-3.5">
                <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]/80" />
                <span className="ml-3 flex items-center gap-2 text-xs text-[#A6B0C9]">
                  <Terminal className="h-3.5 w-3.5" /> lanvaus-open-soc
                </span>
              </div>
              <div className="p-6 font-mono text-[13px] leading-7">
                <p className="text-[#A6B0C9]">$ deploy --stack lanvaus-open-soc --region in</p>
                {[
                  ["Wazuh SIEM & endpoint agents", "deployed"],
                  ["Suricata network detection", "deployed"],
                  ["Elastic / OpenSearch, 180-day retention", "deployed"],
                  ["TheHive case management", "deployed"],
                  ["Grafana compliance dashboards", "deployed"],
                  ["CERT-In reporting workflow", "deployed"],
                ].map(([name, status], i) => (
                  <motion.p key={name} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.18, duration: 0.4 }} className="text-[#DDE3F2]">
                    <span className="text-[#8E9AEA]">[OK]</span> {name}
                    <span className="text-[#A6B0C9]"> …… {status}</span>
                  </motion.p>
                ))}
                <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 1.6, duration: 0.5 }} className="mt-2 text-white">
                  → DPDP &amp; CERT-In control map <span className="text-[#8E9AEA]">synced</span>
                  <span className="ml-1 inline-block h-4 w-2 animate-pulse bg-[#8E9AEA] align-middle" />
                </motion.p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 sm:py-24 sm:px-8 lg:px-16 lg:py-28" data-testid="why-lanvaus">
        <Reveal>
          <Eyebrow data-testid="manifesto-eyebrow">Why Lanvaus</Eyebrow>
          <h2 className="mt-5 max-w-2xl font-display text-[1.75rem] font-bold leading-[1.2] tracking-tight text-white sm:text-4xl sm:leading-tight lg:text-[2.75rem] lg:leading-[1.12]">
            Trust is <span className="text-[#8E9AEA]">engineered</span>, not claimed.
          </h2>
        </Reveal>
        <div className="mt-12 sm:mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.08}>
              <div className="h-full border-t border-white/10 pt-7" data-testid={`why-card-${slug(w.title)}`}>
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg border border-white/10 bg-white/[0.05]">
                  <Icon name={w.icon} className="h-5 w-5 text-[#8E9AEA]" />
                </span>
                <h3 className="mt-6 font-display text-xl font-bold text-white">{w.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#DDE3F2]/90">{w.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <TrustCommitments
        items={TRUST_COMMITMENTS}
        title={{ pre: "How we work with you, ", accent: "stated up front." }}
      />

      <section className="mx-auto max-w-7xl px-5 py-16 sm:py-24 sm:px-8 lg:px-16" data-testid="scenarios-teaser">
        <Reveal>
          <div className="glass-card flex flex-col items-start justify-between gap-6 p-6 sm:p-8 sm:flex-row sm:items-center sm:p-10">
            <div>
              <Eyebrow>How Engagements Run</Eyebrow>
              <h2 className="mt-4 max-w-2xl font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                See what working with us <span className="text-[#8E9AEA]">actually looks like.</span>
              </h2>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-[#DDE3F2] sm:text-base">
                Four representative engagement scenarios: the situation, the scope, and the artifacts the team walked
                away holding. Illustrative, not invented client metrics.
              </p>
            </div>
            <Link to="/case-studies" className="btn-secondary shrink-0" data-testid="home-scenarios-link">
              View engagement scenarios
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-16 sm:pb-24 sm:px-8 lg:px-16" data-testid="industries-section">
        <Reveal>
          <div className="glass-card grid gap-10 p-6 sm:p-10 lg:grid-cols-[1fr_1.1fr] lg:p-12">
            <div>
              <Eyebrow data-testid="industries-eyebrow">Industries We Serve</Eyebrow>
              <h2 className="mt-5 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-[2.1rem] lg:leading-[1.2]">
                Built for the <span className="text-[#B7BFDD]">regulated and the fast-growing.</span>
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-[#DDE3F2] sm:text-base">
                Every industry we serve carries real regulatory exposure. Our engagements are mapped to the frameworks each sector is audited against.
              </p>
              <Link to="/industries" className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-white" data-testid="industries-link">
                Explore industries
                <ArrowUpRight className="h-4 w-4 text-[#8E9AEA]" />
              </Link>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {INDUSTRIES.map((ind) => (
                <Link key={ind.name} to="/industries" className="rounded-lg border border-white/10 bg-[#0B0F1A]/70 px-5 py-4 transition-colors duration-200 hover:border-[#8E9AEA]/40" data-testid={`industry-card-${slug(ind.name)}`}>
                  <p className="text-sm font-semibold text-white">{ind.name}</p>
                  <p className="mt-1 text-xs leading-relaxed text-[#A6B0C9]">{ind.note}</p>
                </Link>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <Methodology
        eyebrow="Our Approach"
        title={{ pre: "One disciplined method, ", accent: "every engagement." }}
        copy="Whether it's a penetration test, a managed SOC or an enterprise security architecture, every Lanvaus engagement follows the same four-phase approach."
        steps={METHODOLOGY_GENERIC}
        showButton={false}
        testId="home-methodology"
      />

      <Resources resources={[]} pagePath="/" />

      <CTABand
        title={{ pre: "Ready to secure ", accent: "your business?" }}
        copy="Get a free consultation with our engineers. No obligations, just a clear assessment of where you stand."
        primary={{ label: "Get a Consultation", to: "/contact" }}
        secondary={{ label: "View All Services", to: "/services" }}
      />
    </div>
  );
}
